import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import DataTable, { DataColumn } from '../../lib/appfuse-react/component/DataTable';
import SelectField from '../../lib/appfuse-react/component/SelectField';
import i18n from '../../lib/appfuse-react/core/i18n';
import code from '../../lib/appfuse-react/core/code';
import candidateService from '../../service/candidate/candidateService';

// Local imports
import { DuplicateGroupCard } from './components';
import { claymorphismColors } from './constants';
import { useStyles } from './styles';
import { translateCampaign } from './utils/helpers';

function Statistics() {
  const classes = useStyles();

  // State
  const [countByCampaign, setCountByCampaign] = useState([]);
  const [groupedDuplicates, setGroupedDuplicates] = useState([]);
  const [ready, setReady] = useState(false);

  // Filter state
  const currentYear = new Date().getFullYear();
  const [yearIndex, setYearIndex] = useState(currentYear.toString());
  const [groupByPhone, setGroupByPhone] = useState(false);

  // Constants
  const MIN_YEAR = 2024;

  // Options
  const yearOptions = [
    { value: "-1", text: i18n.translate("All Years") },
    ...Array.from({ length: 5 }, (_, i) => {
      const year = currentYear - i;
      return year >= MIN_YEAR ? { value: year.toString(), text: year.toString() } : null;
    }).filter(Boolean)
  ];

  const groupByOptions = [
    { value: "name", text: "依姓名分組" },
    { value: "nameAndPhone", text: "依姓名+電話分組" },
  ];

  // Handlers
  const handleYearChange = (event) => {
    const { value } = event;
    setYearIndex(value);
  };

  const handleGroupByChange = (event) => {
    const { value } = event;
    setGroupByPhone(value === "nameAndPhone");
  };

  // Fetch grouped statistics
  const fetchGroupedStatistics = async () => {
    try {
      const result = await candidateService.getGroupedStatistics(yearIndex, groupByPhone);
      const { countByCampaign, groupedDuplicates } = result;

      const convertedCountByCampaign = Object.entries(countByCampaign).map(([k, v]) => {
        return { campaign: translateCampaign(k), count: v };
      });
      setCountByCampaign(convertedCountByCampaign);
      setGroupedDuplicates(groupedDuplicates || []);
    } catch (e) {
      console.error(e);
    }
  };

  // Effects
  useEffect(() => {
    fetchGroupedStatistics();
  }, [yearIndex, groupByPhone]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchGroupedStatistics();
    }, 5 * 1000);
    return () => clearInterval(intervalId);
  }, [yearIndex, groupByPhone]);

  useEffect(() => {
    (async () => {
      await code.assert('campaign');
      setReady(true);
    })();
  }, []);

  // Loading state
  if (!ready) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress style={{ color: claymorphismColors.accent }} />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Filters - outside contentWrapper for proper sticky behavior */}
      <Box className={classes.filterContainer}>
        <Box className={classes.claySelect}>
          <SelectField
            fullWidth
            variant='outlined'
            value={yearIndex}
            label={i18n.translate("Year")}
            options={yearOptions}
            textAccessor={'text'}
            valueAccessor={'value'}
            onChange={handleYearChange}
          />
        </Box>
        <Box className={classes.claySelect}>
          <SelectField
            fullWidth
            variant='outlined'
            value={groupByPhone ? "nameAndPhone" : "name"}
            label="分組方式"
            options={groupByOptions}
            textAccessor={'text'}
            valueAccessor={'value'}
            onChange={handleGroupByChange}
          />
        </Box>
      </Box>

      <Box className={classes.contentWrapper}>
        {/* Registration Count Card */}
        <Box className={`${classes.clayCard} ${classes.clayCardOrange}`}>
          <Typography className={classes.sectionTitle}>
            報名人數統計 Registration Statistics
          </Typography>
          <Box className={classes.tableContainer}>
            <DataTable data={countByCampaign}>
              <DataColumn accessor="campaign" label={i18n.translate("Campaign")} align="center"/>
              <DataColumn accessor="count" label={i18n.translate("Count")} align="center"/>
            </DataTable>
          </Box>
        </Box>

        {/* Duplicate Alert Card */}
        <Box className={`${classes.clayCard} ${classes.clayCardCyan}`}>
          <Typography className={classes.sectionTitle}>
            重複簡歷提醒 Duplicate Alert
          </Typography>

          {groupedDuplicates.length === 0 ? (
            // Empty state
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyStateIcon}>✓</Typography>
              <Typography className={classes.emptyStateText}>
                目前沒有重複簡歷 No duplicates found
              </Typography>
            </Box>
          ) : (
            <Box className={classes.cardsGrid}>
              {groupedDuplicates.map((group, index) => (
                <DuplicateGroupCard key={index} group={group} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Statistics;
